
import { useState, useEffect, useCallback } from 'react'
import { X, Trash2, Save, AlertCircle, ShoppingBag, Truck, RotateCcw, Search } from 'lucide-react'

// Helper to format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
        style: 'currency',
        currency: 'LKR',
        minimumFractionDigits: 2
    }).format(amount || 0)
}

export default function EditOrderContentModal({ isOpen, onClose, orderId, token, onUpdateSuccess }) {
    const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [order, setOrder] = useState(null)
    const [globalThreshold, setGlobalThreshold] = useState(2000)
    const [defaultShippingCost, setDefaultShippingCost] = useState(350)
    const [items, setItems] = useState([]) // Local state for editing
    const [originalShipping, setOriginalShipping] = useState(0)

    // Product Search State
    const [products, setProducts] = useState([])
    const [searchTerm, setSearchTerm] = useState("")
    const [filteredProducts, setFilteredProducts] = useState([])
    const [isProductListOpen, setIsProductListOpen] = useState(false)

    // Address Edit State
    const [isEditingAddress, setIsEditingAddress] = useState(false)
    const [citiesData, setCitiesData] = useState([])
    const [availableDistricts, setAvailableDistricts] = useState([])
    const [availableCities, setAvailableCities] = useState([])
    const [customerDetails, setCustomerDetails] = useState({
        customerName: "", phone: "", addressLine1: "", addressLine2: "", city: "", province: "", zipCode: ""
    })

    const inputStyle = "w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 text-gray-700 placeholder-gray-400 shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none"
    const selectStyle = "w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 text-gray-700 shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none"

    const fetchOrderDetails = useCallback(async () => {
        try {
            setLoading(true)
            const [res, thresholdRes, shippingRes] = await Promise.all([
                fetch(`${BASE_URL}/api/orders/${orderId}`, { headers: { "x-auth-token": token } }),
                fetch(`${BASE_URL}/api/settings/freeDeliveryThreshold`, { headers: { "x-auth-token": token } }).catch(() => null),
                fetch(`${BASE_URL}/api/settings/defaultShippingCost`, { headers: { "x-auth-token": token } }).catch(() => null)
            ])
            const data = await res.json()
            
            if (thresholdRes && thresholdRes.ok) {
                const settingsData = await thresholdRes.json()
                if (settingsData && settingsData.value) {
                    setGlobalThreshold(parseFloat(settingsData.value))
                }
            }
            if (shippingRes && shippingRes.ok) {
                const settingsData = await shippingRes.json()
                if (settingsData && settingsData.value) {
                    setDefaultShippingCost(parseFloat(settingsData.value))
                }
            }

            setOrder(data.order)
            setCustomerDetails({
                customerName: data.order.customerName || "",
                phone: data.order.phone || "",
                addressLine1: data.order.addressLine1 || "",
                addressLine2: data.order.addressLine2 || "",
                city: data.order.city || "",
                province: data.order.province || "",
                zipCode: data.order.zipCode || ""
            })
            setItems(data.orderLines.map(line => ({
                ...line,
                originalQty: line.quantity, // Keep track to detect changes
                isRemoved: false, // Soft remove in UI
                isNew: false
            })))
            setOriginalShipping(data.order.originalShippingCost || defaultShippingCost)
            setLoading(false)
        } catch (err) {
            console.error("Failed to fetch order details", err)
            setLoading(false)
        }
    }, [BASE_URL, orderId, token])

    const fetchProductsAndCities = useCallback(async () => {
        try {
            const [productsRes, citiesRes] = await Promise.all([
                fetch(`${BASE_URL}/api/products`, { headers: { "x-auth-token": token } }),
                fetch(`${BASE_URL}/api/cities`, { headers: { "x-auth-token": token } })
            ]);
            if (productsRes.ok) {
                const data = await productsRes.json()
                setProducts(data)
            }
            if (citiesRes.ok) {
                const data = await citiesRes.json()
                setCitiesData(data)
                const uniqueDistricts = [...new Set(data.map(c => c.district_name))].filter(Boolean).sort()
                setAvailableDistricts(uniqueDistricts)
            }
        } catch (err) {
            console.error("Failed to fetch products or cities", err)
        }
    }, [BASE_URL, token])

    // Filter products based on search term
    useEffect(() => {
        if (searchTerm) {
            const lower = searchTerm.toLowerCase()
            setFilteredProducts(products.filter(p =>
                p.productName.toLowerCase().includes(lower) ||
                p.itemNo.toLowerCase().includes(lower)
            ).slice(0, 10)) // Limit results
            setIsProductListOpen(true)
        } else {
            setFilteredProducts([])
            setIsProductListOpen(false)
        }
    }, [searchTerm, products])

    // Fetch order details and products when modal opens
    useEffect(() => {
        if (isOpen && orderId) {
            setIsEditingAddress(false) // reset state on open
            fetchOrderDetails()
            fetchProductsAndCities()
        }
    }, [isOpen, orderId, fetchOrderDetails, fetchProductsAndCities])

    // Update available cities when editing an existing order's province on load
    useEffect(() => {
        if (citiesData.length > 0 && customerDetails.province) {
            const filteredCities = citiesData
                .filter(c => c.district_name === customerDetails.province)
                .map(c => c.city_name)
                .filter(Boolean)
                .sort()
            setAvailableCities(filteredCities)
        }
    }, [citiesData, customerDetails.province])

    const handleCustomerChange = (e) => {
        const { name, value } = e.target
        if (name === "province") {
            const filteredCities = citiesData
                .filter(c => c.district_name === value)
                .map(c => c.city_name)
                .filter(Boolean)
                .sort()
            setAvailableCities(filteredCities)
            setCustomerDetails({ ...customerDetails, province: value, city: "", zipCode: "" })
        } else if (name === "city") {
            const selectedCityData = citiesData.find(
                c => c.city_name === value && c.district_name === customerDetails.province
            )
            setCustomerDetails({ ...customerDetails, city: value, zipCode: selectedCityData?.postcode || "" })
        } else {
            setCustomerDetails({ ...customerDetails, [name]: value })
        }
    }

    // Calculate live totals based on local state
    const calculateTotals = () => {
        const activeItems = items.filter(i => !i.isRemoved)

        // Subtotal
        const subTotal = activeItems.reduce((sum, item) => {
            // New items might not have lineTotal calculated yet depending on state, 
            // but we can compute: price * qty
            let lineTotal = item.lineTotal;
            if (item.isNew) {
                lineTotal = (item.price || 0) * item.quantity;
            } else {
                // For existing items, unit price derived from original total
                const unitPrice = item.lineTotal / item.originalQty
                lineTotal = unitPrice * item.quantity
            }
            return sum + lineTotal
        }, 0)

        // Shipping Logic: Free if >= threshold
        const shipping = subTotal >= globalThreshold ? 0 : originalShipping

        const total = subTotal + shipping

        return { subTotal, shipping, total }
    }

    const handleQtyChange = (itemId, newQty) => {
        if (newQty < 1) return; // Minimum 1, use delete to remove
        setItems(prev => prev.map(item =>
            item._id === itemId ? { ...item, quantity: parseInt(newQty) } : item
        ))
    }

    const handleRemove = (itemId) => {
        setItems(prev => {
            return prev.map(item => {
                if (item._id === itemId) {
                    if (item.isNew) {
                        // Completely remove new items from list
                        return null
                    }
                    return { ...item, isRemoved: !item.isRemoved }
                }
                return item
            }).filter(Boolean) // Filter out nulls (removed new items)
        })
    }

    const handleAddItem = (product) => {
        const newItem = {
            _id: `new_${Date.now()}`,
            productId: product._id,
            productName: `[${product.productType}] ${product.productName}`,
            variation: product.variation,
            quantity: 1,
            price: product.mrp || 0, // Store unit price for new item calculation
            lineTotal: product.mrp || 0,
            originalQty: 0,
            isNew: true,
            isRemoved: false
        }
        setItems(prev => [...prev, newItem])
        setSearchTerm("")
        setIsProductListOpen(false)
    }

    const handleSave = async () => {
        try {
            setSaving(true)

            // Prepare payload
            const updatedLines = items
                .filter(item => !item.isNew) // Only existing items
                .map(item => {
                    if (item.isRemoved) return { orderLineId: item._id, quantity: 0 }
                    if (item.quantity !== item.originalQty) return { orderLineId: item._id, quantity: item.quantity }
                    return null
                }).filter(Boolean)

            const newOrderLines = items
                .filter(item => item.isNew) // Only new items
                .map(item => ({
                    productId: item.productId,
                    variation: item.variation,
                    quantity: item.quantity
                }))

            if (updatedLines.length === 0 && newOrderLines.length === 0 && !isEditingAddress) {
                onClose()
                return
            }

            // 1. Save Header Details (Address) if editing
            if (isEditingAddress) {
                const addressRes = await fetch(`${BASE_URL}/api/orders/${orderId}/address`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
                    body: JSON.stringify(customerDetails)
                })
                if (!addressRes.ok) throw new Error("Failed to update address details")
            }

            // 2. Save Items
            if (updatedLines.length > 0 || newOrderLines.length > 0) {
                const itemsRes = await fetch(`${BASE_URL}/api/orders/${orderId}/items`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
                    body: JSON.stringify({ updatedLines, newOrderLines })
                })
                const itemData = await itemsRes.json()
                if (!itemsRes.ok) throw new Error(itemData.message || "Failed to update item details")
            }

            onUpdateSuccess(orderId)
            onClose()
        } catch (err) {
            alert(err.message || "Failed to update order")
        } finally {
            setSaving(false)
        }
    }

    const { subTotal, shipping, total } = calculateTotals()

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <ShoppingBag className="w-5 h-5 text-blue-600" />
                            Edit Order Content
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">Modify items for Order #{order?.orderId}</p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setIsEditingAddress(!isEditingAddress)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors border ${isEditingAddress
                                ? 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200'
                                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            {isEditingAddress ? 'Editing Address' : 'Edit Address'}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 space-y-3">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                            <p className="text-gray-500 text-sm">Loading order details...</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-6">

                            {/* Dynamic Panel: Either Edit Address or Edit Items based on state */}
                            {isEditingAddress ? (
                                <div className="bg-gray-50 border border-amber-200 rounded-xl p-5 mb-4 shadow-sm animate-in fade-in slide-in-from-top-4">
                                    <h4 className="text-sm font-bold text-amber-800 uppercase tracking-wider mb-4">Customer Details</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">Customer Name</label>
                                            <input type="text" name="customerName" value={customerDetails.customerName} onChange={handleCustomerChange} className={inputStyle} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">Phone Number</label>
                                            <input type="text" name="phone" value={customerDetails.phone} onChange={handleCustomerChange} className={inputStyle} />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">Address Line 1</label>
                                            <input type="text" name="addressLine1" value={customerDetails.addressLine1} onChange={handleCustomerChange} className={inputStyle} />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">Address Line 2</label>
                                            <input type="text" name="addressLine2" value={customerDetails.addressLine2} onChange={handleCustomerChange} className={inputStyle} />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">District</label>
                                            <select name="province" value={customerDetails.province} onChange={handleCustomerChange} className={selectStyle}>
                                                <option value="">Select District</option>
                                                {availableDistricts.map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">City</label>
                                            <select name="city" value={customerDetails.city} onChange={handleCustomerChange} className={selectStyle} disabled={!customerDetails.province}>
                                                <option value="">{customerDetails.province ? "Select City" : "Select District First"}</option>
                                                {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">Postal Code</label>
                                            <input type="text" name="zipCode" value={customerDetails.zipCode} onChange={handleCustomerChange} className={inputStyle} />
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {/* Add Item Section */}
                            <div className="relative z-50">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Add Item</label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search product to add..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                    />
                                    {isProductListOpen && filteredProducts.length > 0 && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50 animate-in fade-in slide-in-from-top-2">
                                            {filteredProducts.map(product => (
                                                <button
                                                    key={product._id}
                                                    onClick={() => handleAddItem(product)}
                                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between group transition-colors border-b border-gray-50 last:border-0"
                                                >
                                                    <div>
                                                        <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                                                            {product.productName}
                                                        </div>
                                                        <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                                                            <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{product.itemNo}</span>
                                                            <span>Stock: {product.stock}</span>
                                                        </div>
                                                    </div>
                                                    <div className="text-sm font-semibold text-gray-700">
                                                        {formatCurrency(product.mrp)}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>


                            {/* Items List */}
                            <div className="space-y-3">
                                <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2 flex justify-between items-center">
                                    <span>Order Items</span>
                                    <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{items.length} Items</span>
                                </h4>

                                {items.length === 0 && (
                                    <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                        No items in this order.
                                    </div>
                                )}

                                {items.map(item => (
                                    <div
                                        key={item._id}
                                        className={`flex items-center justify-between p-4 rounded-xl border transition-all ${item.isRemoved
                                            ? 'bg-red-50 border-red-100 opacity-75'
                                            : item.isNew
                                                ? 'bg-green-50/50 border-green-100'
                                                : 'bg-white border-gray-100 hover:border-blue-200 hover:shadow-sm'
                                            }`}
                                    >
                                        <div className="flex-1 min-w-0 pr-4">
                                            <div className="flex items-center gap-2">
                                                <p className={`font-medium truncate ${item.isRemoved ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                                    {item.productName}
                                                </p>
                                                {item.isNew && (
                                                    <span className="bg-green-100 text-green-700 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-md tracking-wide">New</span>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md">
                                                    {item.variation}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    Unit: {formatCurrency(item.isNew ? item.price : item.lineTotal / item.originalQty)}
                                                </span>
                                            </div>
                                        </div>

                                        {!item.isRemoved && (
                                            <div className="flex items-center gap-4 shrink-0">
                                                <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-lg border border-gray-200">
                                                    <span className="text-xs text-gray-500 pl-2 font-medium">Qty</span>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={item.quantity}
                                                        onChange={(e) => handleQtyChange(item._id, e.target.value)}
                                                        className="w-12 p-1 bg-white border border-gray-200 rounded text-center text-sm font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => handleRemove(item._id)}
                                                    className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                                    title={item.isNew ? "Remove" : "Delete Item"}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}

                                        {item.isRemoved && (
                                            <button
                                                onClick={() => handleRemove(item._id)}
                                                className="flex items-center gap-1.5 text-blue-600 text-sm font-medium hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors border border-transparent hover:border-blue-100"
                                            >
                                                <RotateCcw className="w-3.5 h-3.5" />
                                                Undo
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Financial Summary */}
                            <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100 space-y-3">
                                <div className="flex justify-between items-center text-sm text-gray-600">
                                    <span>Subtotal</span>
                                    <span className="font-medium text-gray-900">{formatCurrency(subTotal)}</span>
                                </div>

                                <div className="flex justify-between items-center text-sm">
                                    <span className="flex items-center gap-2 text-gray-600">
                                        <Truck className="w-4 h-4" />
                                        Shipping
                                        {shipping === 0 && (
                                            <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide">Free</span>
                                        )}
                                    </span>
                                    <span className={shipping === 0 ? 'text-green-600 font-medium' : 'text-gray-900 font-medium'}>
                                        {formatCurrency(shipping)}
                                    </span>
                                </div>

                                <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                                    <span className="text-base font-bold text-gray-900">New Total</span>
                                    <span className="text-xl font-bold text-blue-600">{formatCurrency(total)}</span>
                                </div>

                                {shipping > 0 && subTotal < globalThreshold && (
                                    <div className="bg-orange-50 text-orange-700 text-xs p-2 rounded-lg flex items-center gap-2 border border-orange-100">
                                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                        Order is below Rs. {globalThreshold.toLocaleString()}. Shipping charge applied.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-600/20 disabled:opacity-70 disabled:cursor-not-allowed transition-all transform active:scale-95"
                    >
                        {saving ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    )
}
